import { Icon } from '@im/ui-lib/zenith/Icon';
import { InputSearch } from '@im/ui-lib/zenith/InputSearch';
import { LoadingIndicator } from '@im/ui-lib/zenith/LoadingIndicator';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { BillingCard } from './internal/BillingCard';
import { useBillingAddressByCNPJ } from '../../../hooks/use-billing-address-by-cnpj';
import { useTranslate } from '@im/react-localization';
import { getFormattedBillingAddress } from './internal/utils';
import htmlparse from 'html-react-parser';

type Props = {
  isType1?: boolean;
  selectedAddress: any;
  setSelectedAddress: (address: any) => void;
};

export function CustomerBillingSelector(props: Props) {
  const { isType1 = false, selectedAddress, setSelectedAddress } = props;
  const translate = useTranslate();
  const [inputValue, setInputValue] = useState('');
  const [enableAddressByCNPJ, setEnableAddressByCNPJ] = useState(false);
  const [customerAddressByCNPJ, setCustomerAddressByCNPJ] =
    useState<any>(selectedAddress);

  const [inputErrorMessage, setInputErrorMessage] = useState<string>('');
  const {
    data: addressByCNPJ,
    error,
    isError,
    isLoading,
    isSuccess,
  } = useBillingAddressByCNPJ(
    inputValue,
    Boolean(inputValue) && !inputErrorMessage && enableAddressByCNPJ
  );

  useEffect(() => {
    if (selectedAddress?.taxExemptNumber) {
      setCustomerAddressByCNPJ(selectedAddress);
    }
  }, [selectedAddress]);

  useEffect(() => {
    if (!isLoading && (isSuccess || isError)) {
      setCustomerAddressByCNPJ(isSuccess ? { ...addressByCNPJ?.[0] } : {});

      if (isError && error) {
        setInputErrorMessage('BillingHeader.CNPJDoesntExist');
      }
      setEnableAddressByCNPJ(false);
    }
  }, [isLoading, isError, addressByCNPJ, error, isSuccess]);

  const validateInput = () => {
    const regex = /^[0-9]+$/;
    const maxLength = 14;
    const length = inputValue.length;

    if (inputValue) {
      if (!regex.test(inputValue)) {
        setInputErrorMessage('BillingHeader.CNPJNumbersValidation');
      } else if (length > maxLength || length < maxLength) {
        setInputErrorMessage('BillingHeader.CNPJDigitsValidation');
      } else {
        setInputErrorMessage('');
        setEnableAddressByCNPJ(true);
      }
    }
  };

  useEffect(() => {
    if (customerAddressByCNPJ?.taxExemptNumber) {
      setSelectedAddress(customerAddressByCNPJ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAddressByCNPJ]);

  const onBillingCardCancel = () => {
    setCustomerAddressByCNPJ({});
    setSelectedAddress({});
    setInputValue('');
  };

  return (
    <Box sx={{ maxHeight: '336px', minHeight: '156px' }}>
      {customerAddressByCNPJ && customerAddressByCNPJ?.taxExemptNumber ? (
        <Stack>
          <Typography
            color="gray.700"
            sx={{
              '&:after': {
                color: '#D92D20',
                content: '" *"',
              },
              pt: 2,
              px: isType1 ? 4 : 0,
            }}
            variant="text-sm-medium"
          >
            {translate('BillingHeader.SearchCNPJNumber')}
          </Typography>
          <Box px={isType1 ? 4 : 0} py={2}>
            <BillingCard
              isCancelRequired={true}
              onBillingCardCancel={onBillingCardCancel}
              subHeaderText={customerAddressByCNPJ?.taxExemptNumber ?? ''}
              summary={getFormattedBillingAddress(customerAddressByCNPJ)}
            />
          </Box>
        </Stack>
      ) : (
        <Box
          display={'flex'}
          flexDirection={'row'}
          padding={isType1 ? '8px 16px' : '0px'}
          sx={{
            alignItems: 'self-start',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box width={'100%'}>
            <InputSearch
              destructive={Boolean(inputErrorMessage)}
              hintText={htmlparse(
                translate(
                  inputErrorMessage
                    ? inputErrorMessage
                    : 'BillingHeader.CNPJHintText'
                )
              )}
              inputValue={inputValue}
              label={translate('BillingHeader.SearchCNPJNumber')}
              onInputSearch={(value: string) => {
                setInputValue(value);
                setInputErrorMessage('');
              }}
              placeholder="XXXXXXXXXXXXXX"
              required={true}
              searchOptions={[]}
              size="small"
              width="100%"
            />
          </Box>
          <Stack pt={'28px'}>
            {!enableAddressByCNPJ ? (
              <Button
                disabled={!inputValue || Boolean(inputErrorMessage)}
                onClick={() => {
                  if (inputValue) {
                    validateInput();
                  }
                }}
                size="small"
                variant="secondary-gray"
              >
                <Icon iconId="ArrowRight" />
              </Button>
            ) : (
              <Box alignItems={'center'} pt={'10px'}>
                <LoadingIndicator
                  color="light"
                  data-component="Template"
                  data-element="LoadingIndicator"
                  data-source-file="LoadingIndicator.stories.tsx"
                  loadingStyle="line-spinner"
                  spinnerSize={20}
                  supportingText=""
                  value={40}
                  variant="indeterminate"
                />
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
